/* ═══════════════════════════════════════════════════════════════
   ProjectDesignBriefService — Project Center R1, Design Brief tab.

   One row per project (migration 051). `applicableCodes` is a jsonb
   array of { code, note } — round-tripped via cache's jsonFields opt,
   same pattern as DashboardViewService's `config` column.

   DesignBriefFieldService (migration 055) is the sibling: an ordered
   list of custom title/value fields a PM can add to the same brief.
   ═══════════════════════════════════════════════════════════════ */

import type { ProjectDesignBrief, DesignBriefField } from '../../shared/entities';
import * as cache from './cloudCache';

const TABLE = 'project_design_briefs';
const opts = { jsonFields: ['applicableCodes'] };

export const ProjectDesignBriefService = {
  /** The one brief row for a project, or null if it hasn't been created yet. */
  getByProject: async (projectId: string): Promise<ProjectDesignBrief | null> => {
    const rows = await cache.query<ProjectDesignBrief>(TABLE, { projectId }, opts);
    return rows[0] || null;
  },

  /** Create or update. cache.save fires the standard 'specforma-sync-complete'
   *  event on completion — no extra wiring needed here. */
  save: async (brief: ProjectDesignBrief): Promise<ProjectDesignBrief> =>
    cache.save(TABLE, brief, opts),
};

const FIELDS_TABLE = 'project_design_brief_fields';

export const DesignBriefFieldService = {
  /** Every custom field for a project, in display order. */
  listForProject: async (projectId: string): Promise<DesignBriefField[]> => {
    const rows = await cache.query<DesignBriefField>(FIELDS_TABLE, { projectId });
    return [...rows].sort((a, b) => a.sortOrder - b.sortOrder);
  },

  /** Create or update one field. cache.save fires 'specforma-sync-complete'. */
  save: async (field: DesignBriefField): Promise<DesignBriefField> =>
    cache.save(FIELDS_TABLE, field),

  remove: async (id: string): Promise<any> => cache.remove(FIELDS_TABLE, id),

  /** Re-sequences a project's fields to match `ids`' order. Reads the
   *  current rows so each save carries its full record (cache.save's
   *  INSERT-on-conflict path only fills columns present in the payload it
   *  is given, and the server's own row is the source of truth for
   *  everything else on the field). */
  reorder: async (projectId: string, ids: string[]): Promise<void> => {
    const current = await DesignBriefFieldService.listForProject(projectId);
    const byId = new Map(current.map((f) => [f.id, f]));
    for (let i = 0; i < ids.length; i++) {
      const field = byId.get(ids[i]);
      if (field && field.sortOrder !== i) {
        await cache.save(FIELDS_TABLE, { ...field, sortOrder: i });
      }
    }
  },
};
